/**
 * Privacy Policy for pcbx.ai / rscircuit Team Platform.
 * Worker serves this as real HTML at `/privacy` so crawlers (Google OAuth
 * verification) are not stuck with an empty SPA shell.
 */

export const PRIVACY_HEADING = "Privacy Policy";
export const PRIVACY_UPDATED = "11 September 2026";

export type PrivacySection = {
  id: string;
  heading: string;
  paragraphs: string[];
  bullets?: string[];
};

export const PRIVACY_SECTIONS: PrivacySection[] = [
  {
    id: "who",
    heading: "Who this covers",
    paragraphs: [
      "This policy is for https://pcbx.ai — the rscircuit Team Platform (hardware collaboration: specs, design files, reviews, issues, and related jobs). It is not a separate legal entity name: the operators of pcbx.ai run this product.",
      "It applies when you visit the site, create an account, sign in with email or with Google / GitHub, or upload project files. Staging hosts on workers.dev are the same product with login skipped for internal testing; this public policy describes production pcbx.ai.",
    ],
  },
  {
    id: "account",
    heading: "Account data",
    paragraphs: [
      "If you create an account we store what you give us so we can identify you and keep you signed in:",
    ],
    bullets: [
      "Email address and display name.",
      "Password hash if you register with email (we do not store the raw password).",
      "Account identifiers and profile fields from Google or GitHub when you use those buttons (typically name, email, and profile image URL — not your Gmail, Drive, or GitHub repositories unless you later connect a repo).",
      "Organization and project membership (which teams and boards you can open).",
    ],
  },
  {
    id: "oauth",
    heading: "Google and GitHub sign-in",
    paragraphs: [
      "“Continue with Google” and “Continue with GitHub” are only for signing in to pcbx.ai. We receive the profile the provider shares for that OAuth client (email, name, and photo URL when the provider sends them). We use that to create or look up your pcbx.ai user record and a session.",
      "We do not request Gmail, Google Drive, Calendar, or similar scopes. We do not post to Google or GitHub on your behalf from the login buttons. Optional GitHub App repo bind is a separate, explicit project setting and is not part of Google sign-in.",
      "Google and GitHub process your identity under their own policies. Revoking access in your Google or GitHub account settings stops new sign-ins from that provider; it does not automatically delete the pcbx.ai account you already created.",
    ],
  },
  {
    id: "files",
    heading: "Project files and product data",
    paragraphs: [
      "The product is a team workspace. Content you or your teammates upload or generate is stored so the Mission can run:",
    ],
    bullets: [
      "Hardware archives (for example KiCad zips), schematics, diffs, reviews, issues, milestones, and job artifacts.",
      "Optional GitHub repository metadata if a project owner binds a repo (installation id, repo name, default branch) so we can import or sync — not your full GitHub account.",
      "Messages and job payloads sent to design/agent services so those jobs can run on your project files.",
    ],
  },
  {
    id: "cookies",
    heading: "Cookies and session",
    paragraphs: [
      "We use first-party session cookies (HttpOnly, SameSite=Lax, Secure on HTTPS) so the browser can stay signed in. That is a login session, not an advertising cookie.",
      "We do not use third-party advertising or analytics pixels on pcbx.ai. We do not sell personal information.",
      "The marketing pages load webfonts from Google Fonts. Google may see the request (IP address and user agent) when your browser fetches those fonts. The Privacy Policy document at /privacy is served as static HTML and does not load those fonts.",
    ],
  },
  {
    id: "hosting",
    heading: "Where data lives",
    paragraphs: [
      "pcbx.ai is hosted on Cloudflare (Workers, D1, R2, and related edge services). Account identity lives in the auth database; project files and artifacts live in the workbench database and object storage. Cloudflare processes that data as our processor / hosting provider.",
      "Request logs typical of any HTTPS service (IP address, user agent, URL, timestamps) may be collected for security and reliability. Session records may store IP and user agent when you sign in.",
    ],
  },
  {
    id: "use",
    heading: "How we use the data",
    paragraphs: [
      "We use this information to operate the product you asked for: authenticate you, show your orgs and projects, store and process board files, run jobs you start, and keep the service secure.",
      "We do not use Google account data to advertise. We do not share your project files with other customers. Teammates you add to an org or project can see that project’s content — that is the point of a team platform.",
    ],
  },
  {
    id: "share",
    heading: "Who else sees it",
    paragraphs: [
      "We share data only as needed to run the service:",
    ],
    bullets: [
      "Identity providers (Google, GitHub) for the sign-in you chose.",
      "Cloudflare, for hosting and storage.",
      "If required by law, or to protect the service against abuse.",
    ],
  },
  {
    id: "retention",
    heading: "Retention, access, and deletion",
    paragraphs: [
      "We keep account and project data while the account and projects exist so the workspace keeps working.",
      "This version of the product does not yet ship a self-serve “delete my account” button. To request a copy of the account data we hold, or deletion of your pcbx.ai account and hosted project files we control, sign in and contact the operators using the email on that account (or the Google / GitHub address you used to register). Say that you want access or deletion under this Privacy Policy.",
      "We will process identity deletion and hosted files we store. Google and GitHub keep their own records. Backups and security logs may lag for a short period. We may retain a minimal record if we must (for example a ban for abuse).",
    ],
  },
  {
    id: "children",
    heading: "Children",
    paragraphs: [
      "pcbx.ai is a professional hardware tool. It is not directed at children under 13, and we do not knowingly collect personal information from children under 13.",
    ],
  },
  {
    id: "changes",
    heading: "Changes",
    paragraphs: [
      "If this policy changes in a material way we will update this page and the date above. The current policy is always at https://pcbx.ai/privacy.",
    ],
  },
  {
    id: "contact",
    heading: "Contact",
    paragraphs: [
      "Operators of pcbx.ai / rscircuit Team Platform. For privacy requests, use the email address on your pcbx.ai account (or the Google / GitHub address you used to sign in).",
    ],
  },
];

export function isPrivacyPath(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  return p === "/privacy";
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function renderSection(section: PrivacySection): string {
  const bullets = section.bullets?.length
    ? `<ul>${section.bullets.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>`
    : "";
  const paragraphs = section.paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join("");
  return `<section id="${escapeHtml(section.id)}"><h2>${escapeHtml(section.heading)}</h2>${paragraphs}${bullets}</section>`;
}

/** Full HTML document — no JS, so curl/Google see the policy text. */
export function privacyPolicyHtml(): string {
  const sections = PRIVACY_SECTIONS.map(renderSection).join("");
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(PRIVACY_HEADING)} · pcbx.ai</title>
  <link rel="privacy-policy" href="/privacy" />
  <style>
    body { margin: 0; background: #000; color: #f0f0fa; font: 16px/1.55 system-ui, -apple-system, Segoe UI, sans-serif; }
    header, main, footer { max-width: 720px; margin: 0 auto; padding: 24px 24px 8px; }
    a { color: #f0f0fa; }
    h1 { font-size: 32px; letter-spacing: -0.02em; margin: 0 0 8px; }
    h2 { font-size: 18px; margin: 28px 0 8px; }
    p, li { color: rgba(240,240,250,0.82); }
    .muted { color: rgba(240,240,250,0.5); font-size: 13px; }
    footer { padding-bottom: 48px; border-top: 1px solid rgba(255,255,255,0.12); margin-top: 32px; }
  </style>
</head>
<body>
  <header>
    <p class="muted"><a href="/">pcbx.ai</a> · rscircuit Team Platform</p>
    <h1>${escapeHtml(PRIVACY_HEADING)}</h1>
    <p class="muted">Effective ${escapeHtml(PRIVACY_UPDATED)}</p>
  </header>
  <main>
    ${sections}
  </main>
  <footer>
    <p><a href="/privacy">Privacy Policy</a> · <a href="/">Home</a></p>
  </footer>
</body>
</html>
`;
}

/** Document GET/HEAD `/privacy` — never an empty SPA shell. */
export function maybePrivacyDocument(request: Request): Response | null {
  if (request.method !== "GET" && request.method !== "HEAD") return null;
  let pathname = "/";
  try {
    pathname = new URL(request.url).pathname;
  } catch {
    return null;
  }
  if (!isPrivacyPath(pathname)) return null;
  const html = privacyPolicyHtml();
  return new Response(request.method === "HEAD" ? null : html, {
    status: 200,
    headers: {
      "content-type": "text/html; charset=utf-8",
      "cache-control": "public, max-age=300",
    },
  });
}
